import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ApiError, api, setUnauthorizedHandler } from "../api/client";
import type { PortalUser } from "./types";

/**
 * Portal session state, mirrored from the server.
 *
 * A deliberate twin of the console's `AuthProvider` rather than a generalization of
 * it. The two differ in the cookie the browser sends, the endpoints they call and
 * the identity they hold, and the one thing a shared abstraction would buy — fewer
 * lines — is worth less than the guarantee that no component can be handed an
 * operator's identity and a client's interchangeably. There is also no `can()` here:
 * a portal role gates nothing this build renders, and a client-side role check that
 * exists but means nothing is worse than none.
 *
 * Nothing is persisted. The cookie is the session, so on a hard refresh the answer
 * comes from `/api/portal/auth/me` — the client cannot believe in a session the
 * server has already revoked, which it does on every password reset, role change
 * and deactivation.
 */

interface PortalAuthState {
  /** `undefined` while the initial /me is in flight, `null` when signed out. */
  user: PortalUser | null | undefined;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Drop the local session without calling logout.
   *
   * For the self-service password change, which revokes every session server-side
   * including the one that made the request: the cookie is already cleared by that
   * response, so a logout call would just be a 401.
   */
  endSession: () => void;
}

const PortalAuthContext = createContext<PortalAuthState | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUser | null | undefined>(undefined);

  useEffect(() => {
    // Safe to own this globally: only one of the two surfaces is ever mounted, so
    // the console's provider is not registered at the same time.
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ user: PortalUser }>("/api/portal/auth/me")
      .then((res) => {
        if (!cancelled) setUser(res.user);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await api.post<{ user: PortalUser }>("/api/portal/auth/login", { email, password });
    setUser(res.user);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post("/api/portal/auth/logout");
    } catch (err) {
      // A failed logout still ends the local session: the cookie expires on its own,
      // and leaving someone staring at a portal they asked to leave is the worse
      // outcome.
      if (!(err instanceof ApiError)) throw err;
    }
    setUser(null);
  }, []);

  const endSession = useCallback(() => setUser(null), []);

  const value = useMemo<PortalAuthState>(
    () => ({ user, signIn, signOut, endSession }),
    [user, signIn, signOut, endSession],
  );
  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

export function usePortalAuth(): PortalAuthState {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth must be used inside <PortalAuthProvider>");
  return ctx;
}
